<template>
  <el-drawer v-dialog-window :title="form.id ? '编辑地址簿' : '新增地址簿'" :visible.sync="drawerVisible" direction="rtl" size="500px" append-to-body>
    <div class="address-book-drawer">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <el-form-item label="地址名称" prop="poiName">
          <el-input v-model.trim="form.poiName" maxlength="100" placeholder="例如：宁波北仑装货点" />
        </el-form-item>
        <el-form-item label="省 / 市 / 区">
          <el-cascader v-model="form.areaCodes" :options="regionOptions" clearable class="full-width" placeholder="请选择地区" @change="onAreaChange" />
        </el-form-item>
        <el-form-item label="详细地址" prop="address">
          <div class="address-book-drawer__address">
            <el-input v-model.trim="form.address" maxlength="200" placeholder="请输入详细地址" />
            <el-button type="text" @click="resolveCoordinate">解析坐标</el-button>
            <el-button type="text" @click="locationPickerVisible = true">地图选点</el-button>
          </div>
        </el-form-item>
        <el-form-item label="坐标" prop="lonLat">
          <coordinate-input v-model="form.lonLat" maxlength="40" placeholder="经度,纬度" />
        </el-form-item>
        <div class="address-book-drawer__grid">
          <el-form-item label="联系人"><el-input v-model.trim="form.contactName" maxlength="64" placeholder="请输入联系人" /></el-form-item>
          <el-form-item label="联系电话"><el-input v-model.trim="form.contactPhone" maxlength="20" placeholder="请输入联系电话" /></el-form-item>
        </div>
        <el-form-item label="备注"><el-input v-model.trim="form.userRemark" maxlength="200" placeholder="请输入备注" /></el-form-item>
      </el-form>
      <div class="address-book-drawer__footer">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button v-permission="form.id ? 'address:edit' : 'address:add'" type="primary" :loading="saving" @click="submit">保存</el-button>
      </div>
    </div>
    <waybill-location-picker
      :visible.sync="locationPickerVisible"
      :lon-lat="form.lonLat"
      :address="form.address"
      :area-codes="form.areaCodes"
      :area-name="areaName"
      @confirm="applyPickedLocation"
    />
  </el-drawer>
</template>

<script>
import { regionData } from 'element-china-area-data'
import { createAddressBook, isRequestSuccess, updateAddressBook } from './lingdongApi'
import WaybillLocationPicker from './WaybillLocationPicker.vue'
import CoordinateInput from './CoordinateInput.vue'
import { getAreaName, normalizeAreaCodes, stripAreaPrefix, buildFullAddress, getAreaCodesByAdcode, getAreaCodesByAddress } from './locationArea'
import { gcj02PointToWgs84LonLat, parseLonLat } from './coordinate'
import { setAmapSecurityConfig, loadAmapSDK } from '@/utils/amapConfig'

const createForm = () => ({ id: null, poiName: '', areaCodes: [], address: '', lonLat: '', contactName: '', contactPhone: '', userRemark: '' })

export default {
  name: 'AddressBookDrawer',
  components: { WaybillLocationPicker, CoordinateInput },
  props: {
    visible: { type: Boolean, default: false },
    addressBook: { type: Object, default: null }
  },
  data() {
    return {
      drawerVisible: false,
      locationPickerVisible: false,
      saving: false,
      formSession: 0,
      coordinateRequestId: 0,
      regionOptions: regionData,
      form: createForm(),
      rules: {
        poiName: [{ required: true, message: '请输入地址名称', trigger: 'blur' }],
        address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }],
        lonLat: [{ required: true, validator: this.validateLonLat, trigger: 'blur' }]
      }
    }
  },
  computed: {
    areaName() { return getAreaName(this.form.areaCodes) }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.drawerVisible = value
        if (value) this.initForm()
        else this.invalidateRequests()
      }
    },
    addressBook() { if (this.visible) this.initForm() },
    drawerVisible(value) {
      if (!value) this.invalidateRequests()
      this.$emit('update:visible', value)
    }
  },
  beforeDestroy() { this.invalidateRequests() },
  methods: {
    invalidateRequests() {
      this.formSession += 1
      this.coordinateRequestId += 1
      this.locationPickerVisible = false
      this.saving = false
    },
    initForm() {
      this.invalidateRequests()
      const source = this.addressBook || {}
      let areaCodes = normalizeAreaCodes(source.areaCodes)
      if (areaCodes.length !== 3) {
        const nameCodes = getAreaCodesByAddress(source.areaName)
        const addressCodes = nameCodes.length ? nameCodes : getAreaCodesByAddress(source.address)
        if (addressCodes.length) areaCodes = addressCodes
      }
      this.form = {
        id: source.id || null,
        poiName: source.poiName || '',
        areaCodes,
        address: stripAreaPrefix(source.address, getAreaName(areaCodes)),
        lonLat: source.longitude !== undefined && source.longitude !== null && source.latitude !== undefined && source.latitude !== null ? `${source.longitude},${source.latitude}` : '',
        contactName: source.contactName || '',
        contactPhone: source.contactPhone || '',
        userRemark: source.userRemark || source.remark || ''
      }
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    onAreaChange() {
      this.form.address = stripAreaPrefix(this.form.address, this.areaName)
    },
    parseLonLat(value) {
      return parseLonLat(value)
    },
    validateLonLat(rule, value, callback) {
      callback(this.parseLonLat(value) ? undefined : new Error('请输入正确的经度,纬度'))
    },
    clearLocationValidation() {
      const form = this.form
      this.$nextTick(() => {
        if (form !== this.form || !this.$refs.form) return
        const fields = []
        if (String(form.address || '').trim()) fields.push('address')
        if (this.parseLonLat(form.lonLat)) fields.push('lonLat')
        if (fields.length) this.$refs.form.clearValidate(fields)
      })
    },
    async resolveCoordinate() {
      const address = buildFullAddress(this.form.areaCodes, this.form.address)
      if (!address) { this.$message.warning('请先选择省市区或填写详细地址'); return }
      const form = this.form
      const requestId = ++this.coordinateRequestId
      const originalAddress = form.address
      const originalCodes = form.areaCodes.join(',')
      const originalLonLat = form.lonLat
      const isCurrent = () => form === this.form && requestId === this.coordinateRequestId && form.address === originalAddress && form.areaCodes.join(',') === originalCodes && form.lonLat === originalLonLat
      try {
        setAmapSecurityConfig()
        await loadAmapSDK(['AMap.Geocoder'])
        if (!isCurrent()) return
        const geocode = await new Promise((resolve) => {
          new window.AMap.Geocoder().getLocation(address, (status, result) => {
            resolve(status === 'complete' && result.geocodes && result.geocodes.length ? result.geocodes[0] : null)
          })
        })
        if (!isCurrent()) return
        if (!geocode || !geocode.location) { this.$message.warning('未解析到坐标，请使用地图选点'); return }
        this.form.lonLat = gcj02PointToWgs84LonLat(geocode.location.getLng(), geocode.location.getLat())
        const areaCodes = getAreaCodesByAdcode(geocode.adcode, geocode)
        if (areaCodes.length) this.form.areaCodes = areaCodes
        this.form.address = stripAreaPrefix(this.form.address, this.areaName)
        this.clearLocationValidation()
        this.$message.success('坐标已解析')
      } catch (e) {
        if (isCurrent()) this.$message.warning('坐标解析失败，请使用地图选点')
      }
    },
    applyPickedLocation(location) {
      this.coordinateRequestId += 1
      this.form.lonLat = location.lonLat || ''
      if (Array.isArray(location.areaCodes) && location.areaCodes.length) this.form.areaCodes = normalizeAreaCodes(location.areaCodes)
      if (location.address) this.form.address = stripAreaPrefix(location.address, this.areaName || location.areaName)
      this.clearLocationValidation()
    },
    buildPayload() {
      const point = this.parseLonLat(this.form.lonLat)
      let areaCodes = normalizeAreaCodes(this.form.areaCodes)
      if (areaCodes.length !== 3) {
        const addressCodes = getAreaCodesByAddress(this.form.address)
        if (addressCodes.length) areaCodes = addressCodes
      }
      const areaName = getAreaName(areaCodes)
      return {
        poiName: this.form.poiName,
        address: areaCodes.length === 3 ? stripAreaPrefix(this.form.address, areaName) : this.form.address,
        longitude: point[0],
        latitude: point[1],
        areaCodes: areaCodes.join(','),
        areaName,
        contactName: this.form.contactName,
        contactPhone: this.form.contactPhone,
        userRemark: this.form.userRemark
      }
    },
    async submit() {
      if (this.saving) return
      const session = this.formSession
      const form = this.form
      const isCurrent = () => session === this.formSession && form === this.form
      this.saving = true
      try {
        try { await this.$refs.form.validate() } catch (e) { return }
        if (!isCurrent()) return
        const payload = this.buildPayload()
        const res = await (form.id ? updateAddressBook(form.id, payload) : createAddressBook(payload))
        if (!isCurrent()) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '保存失败')
        this.$message.success('地址簿已保存')
        this.$emit('saved', res.data || { ...payload, id: form.id })
        this.drawerVisible = false
      } catch (e) {
        if (isCurrent()) this.$message.error(e.msg || e.message || '保存失败')
      } finally {
        if (isCurrent()) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.address-book-drawer { display: flex; min-height: calc(100vh - 54px); flex-direction: column; padding: 20px; box-sizing: border-box; }
::v-deep .dialog-window .address-book-drawer { min-height: 100%; }
.full-width { width: 100%; }
.address-book-drawer__address { display: flex; align-items: center; gap: 8px; }
.address-book-drawer__address ::v-deep .el-input { flex: 1; min-width: 0; }
.address-book-drawer__address .el-button { flex: none; margin: 0; padding: 2px 0; white-space: nowrap; }
.address-book-drawer__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.address-book-drawer__footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: auto; padding-top: 20px; }
</style>
